import { combineReducers, configureStore, Middleware } from "@reduxjs/toolkit";
import { zettleAuthReducer, logout, restore, ZettleAuthStatus } from "@slices";
import { selectZettleAuth, initialState as zettleAuthInitialState, ZettleAuthState, selectIsTokenExpired } from "../slices/ZettleAuth/ZettleAuth";
import { initialState as pageStateInitialState } from "@slices/PageState/PageState";
import { secureStorageSet, secureStorageGet, secureStorageClear } from "../services/crypto";

import { zettlePurchase } from "@slices";
import { pageStateReducer } from "@slices";
import { ErrorCategory, ErrorSeverity, createError, logError } from '@services/errorHandling';

const rootReducer = combineReducers({
    zettleAuth: zettleAuthReducer,
    [zettlePurchase.reducerPath]: zettlePurchase.reducer,
    pageState: pageStateReducer
});

const sessionKey = "zettleAuth";

const configureStorePersistence = (store: Store) => {
    let currentState: ZettleAuthState | null = null;
    let isProcessing = false;

    async function handleChange() {
        // Prevent re-entrancy
        if (isProcessing) return;

        const nextState = selectZettleAuth(store.getState());

        // Deep comparison to avoid unnecessary processing
        if (JSON.stringify(nextState) === JSON.stringify(currentState)) {
            return;
        }

        isProcessing = true;
        currentState = nextState;

        try {
            // Handle logout action - clear storage
            if (nextState.status === ZettleAuthStatus.Unauthenticated) {
                secureStorageClear();
                if (process.env.NODE_ENV === 'development') {
                    console.debug("Cleared session storage on logout");
                }
                return;
            }

            // Check for token expiration ONLY when authenticated
            if (nextState.status === ZettleAuthStatus.Authenticated && nextState.token) {
                if (selectIsTokenExpired(store.getState())) {
                    if (process.env.NODE_ENV === 'development') {
                        console.debug("Token expired, logging out");
                    }
                    store.dispatch(logout());
                    return;
                }
            }

            // Save encrypted state for authenticated and waiting states
            if (nextState !== null && (nextState.token || nextState.status === ZettleAuthStatus.WaitingForCode)) {
                try {
                    // For authenticated states, only save essential data
                    // For waiting states, save OAuth flow data
                    const stateToSave: ZettleAuthState = nextState.token
                        ? {
                              status: nextState.status,
                              token: nextState.token,
                              refresh_token: nextState.refresh_token,
                              expires_at: nextState.expires_at,
                          }
                        : {
                              status: nextState.status,
                              nonce: nextState.nonce,
                              code_challenge: nextState.code_challenge,
                              code_verifier: nextState.code_verifier,
                          };

                    await secureStorageSet(sessionKey, JSON.stringify(stateToSave));
                    if (process.env.NODE_ENV === 'development') {
                        console.debug("Saved encrypted session data");
                    }
                } catch (error) {
                    const appError = createError(
                        ErrorCategory.STORAGE,
                        ErrorSeverity.WARNING,
                        "Failed to save session data",
                        error,
                        { status: nextState.status, hasToken: !!nextState.token }
                    );
                    logError(appError);
                }
            }
        } finally {
            isProcessing = false;
        }
    }

    const unsubscribe = store.subscribe(() => {
        handleChange().catch((error) => {
            const appError = createError(
                ErrorCategory.STORAGE,
                ErrorSeverity.WARNING,
                "Error in store persistence handler",
                error
            );
            logError(appError);
        });
    });

    handleChange().catch((error) => {
        const appError = createError(
            ErrorCategory.STORAGE,
            ErrorSeverity.WARNING,
            "Error hydrating initial state",
            error
        );
        logError(appError);
    });
    return unsubscribe;
};

/**
 * Asynchronously restores auth state from encrypted storage.
 * This is called after store creation to hydrate the state.
 */
const restoreStateFromPersistence = async (store: Store): Promise<void> => {
    try {
        const encryptedData = await secureStorageGet(sessionKey);
        if (encryptedData !== null) {
            const zettleData: ZettleAuthState = JSON.parse(encryptedData);

            // Check if restored token is expired
            if (zettleData.expires_at && Date.now() >= zettleData.expires_at) {
                if (process.env.NODE_ENV === 'development') {
                    console.debug("Restored token is expired, clearing storage");
                }
                secureStorageClear();
                return;
            }

            // Restore the auth state
            store.dispatch(restore(zettleData));

            if (process.env.NODE_ENV === 'development') {
                console.log("Loaded encrypted ZettleAuth data from session storage");
            }
        }
    } catch (error) {
        const appError = createError(
            ErrorCategory.STORAGE,
            ErrorSeverity.WARNING,
            "Failed to restore session data, clearing storage",
            error
        );
        logError(appError);
        secureStorageClear();
    }
};

export function setupStore() {
    const preloadedState = {
        zettleAuth: zettleAuthInitialState,
        pageState: pageStateInitialState,
        zettlePurchase: {}
    } as RootState;

    const logger: Middleware<object, RootState> = () => (next) => (action) => {
        if (process.env.NODE_ENV === 'development') {
            // Log action type only, not full action or state (may contain tokens)
            if (typeof action === 'object' && action !== null && 'type' in action) {
                console.debug("Action:", action.type);
            }
        }
        return next(action);
    };

    if (process.env.NODE_ENV === 'development') {
        console.log("preloadedState", preloadedState);
    }

    const middlewares = process.env.NODE_ENV === 'development'
        ? [logger, zettlePurchase.middleware]
        : [zettlePurchase.middleware];

    const store = configureStore({
        reducer: rootReducer,
        preloadedState,
        middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(...middlewares),
    });

    // Restore state first, THEN set up persistence
    // This prevents clearing the storage before restoration completes
    restoreStateFromPersistence(store)
        .then(() => {
            configureStorePersistence(store);
        })
        .catch((error) => {
            const appError = createError(
                ErrorCategory.STORAGE,
                ErrorSeverity.WARNING,
                "Failed to initialize store persistence",
                error
            );
            logError(appError);
        });

    return store;
}

export type RootState = ReturnType<typeof rootReducer>;
export type Store = ReturnType<typeof setupStore>;
export type AppDispatch = Store["dispatch"];
